import React from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, ShoppingCart, TrendingUp, Wallet, Factory, CheckCircle2, Receipt, AlertTriangle, Plus, Shirt,
  CreditCard, BarChart3, ChevronRight, Landmark,
} from "lucide-react";
import PushPanel from "@/components/PushPanel";
import { useAuth } from "@/context/AuthContext";
import { apiDashboardOwner, formatApiErrorDetail } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { zonaSingkat } from "@/lib/waktu";
import {
  banding, batangOmzet, belumAdaData, inisial, jamToko, judulWaktu, labelBulan, labelMinggu, labelPeriode, lalu,
  persen, salam, totalTindakan,
} from "@/lib/dashboardOwner";

// Dashboard Owner (owner 2026-10-11/12, mockup v2, docs/rencana/DASHBOARD-OWNER.md).
// Every number comes from GET /dashboard/owner (owner only, checked by the server).
// No animation; reloads every minute while open.

const KARTU = "rounded-2xl border border-slate-800 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

const KotakBanner = ({ Icon, label, nilai, catatan, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/15 bg-white/10 p-3 sm:p-4 min-w-0">
    <div className="flex items-center gap-2 text-xs sm:text-[13px] leading-tight text-[#DDE2FF]">
      <span className="inline-flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-lg bg-white/15"><Icon size={15} className="text-white" /></span>
      {label}
    </div>
    <div className="mt-2 sm:mt-3 text-lg min-[400px]:text-xl sm:text-[28px] font-extrabold text-white tabular-nums break-words">{nilai}</div>
    <div className="mt-1 text-xs text-[#C9CEFF]">{catatan}</div>
  </div>
);

const Chip = ({ b, label }) => {
  if (b.teks === "belum ada data") {
    return <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] font-semibold text-slate-500">vs bulan lalu: —</span>;
  }
  const warna = { naik: "bg-[#E7F6EF] text-[#047857]", turun: "bg-[#FDECEA] text-[#B42318]", netral: "bg-slate-100 text-slate-500" }[b.arah];
  return <span className={`rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${warna}`}>{label ? `${b.teks} ${label}` : b.teks}</span>;
};

const KartuAngka = ({ Icon, ikonWarna, chip, nilai, judul, sub, testid, children }) => (
  <div data-testid={testid} className={`${KARTU} flex flex-col`}>
    <div className="flex items-start justify-between gap-2">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${ikonWarna}`}><Icon size={18} /></span>
      {chip}
    </div>
    <div className="mt-4 text-[26px] font-extrabold text-soq-biru-tua tabular-nums break-words">{nilai}</div>
    <div className="text-sm font-semibold text-slate-200">{judul}</div>
    <div className="text-xs text-slate-500">{sub}</div>
    {children}
  </div>
);

const Bilah = ({ nilai, warna }) => (
  <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
    <div className={`h-full rounded-full ${warna}`} style={{ width: `${nilai}%` }} />
  </div>
);

const judulBaris = (r) => (r.bulan ? labelBulan(r.bulan) : labelMinggu(r.dari, r.sampai));

function Omzet({ d }) {
  const [mode, setMode] = React.useState("tahun");
  const tahun = mode === "tahun";
  const rows = batangOmzet(tahun ? d.omzet_tahun.per_bulan : d.omzet_bulan.per_minggu);
  const ringkas = tahun ? d.omzet_tahun.ringkas : d.omzet_bulan.ringkas;
  const label = tahun ? "tahun ini" : "bulan ini";
  const kosong = belumAdaData(d.data_pertama);
  return (
    <section data-testid="do-omzet" className={KARTU}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-bold text-soq-biru-tua">Omzet</h2>
          <p className="text-xs text-slate-500">{tahun ? "Per bulan" : "Per minggu (Senin–Minggu)"}, tanpa nota batal · batang oranye = berjalan</p>
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-slate-800 text-sm">
          {[["bulan", "Bulan ini"], ["tahun", "Tahun ini"]].map(([k, t]) => (
            <button key={k} type="button" data-testid={`do-omzet-mode-${k}`} onClick={() => setMode(k)}
              className={`px-3.5 py-2 font-medium ${mode === k ? "bg-soq-biru text-white" : "bg-white text-slate-200"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {rows.map((r) => r.kosong ? (
          <div key={`kosong-${r.akhir.bulan || r.akhir.dari}`} data-testid="do-omzet-kosong">
            <div className="text-sm font-semibold text-slate-200">{judulBaris(r.awal)}{r.awal !== r.akhir && <> – {judulBaris(r.akhir)}</>}</div>
            <div className="mt-1.5 rounded-md border border-slate-800 px-3 py-1.5 text-xs text-slate-500">{kosong}</div>
          </div>
        ) : (
          <div key={r.bulan || r.dari} data-testid="do-omzet-baris">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-semibold text-slate-200">
                {judulBaris(r)}
                <span className="font-normal text-slate-500"> · {r.order} order</span>
              </span>
              <span className="font-bold tabular-nums text-soq-biru-tua">{rupiah(r.omzet)}</span>
            </div>
            <div className="mt-1.5 h-6 rounded-md bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-md ${r.berjalan ? "bg-soq-oranye" : "bg-soq-biru"}`} style={{ width: `${Math.max(r.lebar, r.omzet ? 2 : 0)}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="do-omzet-ringkas">
        {[
          [TrendingUp, ringkas.omzet, `Omzet ${label}`, "bg-[#EEF1FF]", true],
          [Wallet, ringkas.uang_masuk, `Uang masuk ${label}`, "bg-[#E7F6EF]", true, "termasuk kas manual"],
          [BarChart3, ringkas.rata_per_hari, "Rata-rata per hari", "bg-[#FFF4E0]", true],
          [ShoppingCart, ringkas.order, `Order ${label}`, "bg-slate-100", false],
        ].map(([Icon, v, t, bg, uang, ket]) => (
          <div key={t} className={`rounded-xl p-3 text-center ${bg}`}>
            <Icon size={16} className="mx-auto text-soq-biru" />
            <div className="mt-1 text-base font-extrabold tabular-nums text-soq-biru-tua break-words">{v === null ? "—" : uang ? rupiah(v) : v}</div>
            <div className="text-[11.5px] text-slate-500">{t}</div>
            {ket && <div className="text-[10.5px] text-slate-500">{ket}</div>}
          </div>
        ))}
      </div>
      {ringkas.omzet === null && <p className="mt-2 text-xs text-slate-500">{kosong}</p>}
    </section>
  );
}

const AKT = {
  ORDER: { Icon: ShoppingCart, warna: "bg-[#FFF4E0] text-[#7A4500]", judul: "Order masuk" },
  PEMBAYARAN: { Icon: CreditCard, warna: "bg-[#EEF1FF] text-soq-biru", judul: "Pembayaran masuk" },
  SELESAI_POS: { Icon: CheckCircle2, warna: "bg-[#E7F6EF] text-[#047857]", judul: "Selesai di pos" },
  REJECT_QC: { Icon: AlertTriangle, warna: "bg-[#FDECEA] text-[#B42318]", judul: "Reject QC" },
  LAPOR_MASALAH: { Icon: AlertTriangle, warna: "bg-[#FFF1E6] text-[#B54708]", judul: "Lapor masalah" },  // v2.17
};

function Aktivitas({ d }) {
  const kini = Date.parse(d.waktu) || Date.now();
  return (
    <section data-testid="do-aktivitas" className={KARTU}>
      <h2 className="text-[17px] font-bold text-soq-biru-tua">Aktivitas terbaru</h2>
      <p className="text-xs text-slate-500">Order, pembayaran, pos selesai, reject QC · 7 hari terakhir</p>
      {d.aktivitas.length === 0 && <p className="mt-4 text-sm text-slate-500">Belum ada aktivitas 7 hari terakhir.</p>}
      <ul className="mt-3 divide-y divide-slate-800">
        {d.aktivitas.map((a, i) => {
          const t = AKT[a.jenis];
          return (
            <li key={`${a.jenis}-${a.nomor}-${i}`} className="flex gap-3 py-3" data-testid={`do-aktivitas-${a.jenis}`}>
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${t.warna}`}><t.Icon size={16} /></span>
              <div className="min-w-0 text-sm">
                <div className="text-slate-200">
                  <b>{t.judul}</b>{a.jenis === "SELESAI_POS" && <> {a.pos}</>}{" "}
                  <span className="font-mono">{a.faktur || a.nomor}</span>
                  {a.jenis === "ORDER" && a.customer && <> · {a.customer}</>}
                  {a.jenis === "PEMBAYARAN" && a.cara && <> · {a.cara}</>}
                  {a.jenis === "LAPOR_MASALAH" && <> · {a.asal} → {a.penyebab}{a.qty ? <> · {a.qty}</> : null}</>}
                  {a.jenis === "REJECT_QC" && <>{a.penyebab && <> · penyebab {a.penyebab}</>}{a.qty ? <> · {a.qty} pcs</> : null}</>}
                </div>
                {a.jenis === "PEMBAYARAN" && <div className="font-bold text-[#047857] tabular-nums">{rupiah(a.jumlah)}</div>}
                <div className="text-xs text-slate-500">{lalu(a.waktu, kini)}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const CEPAT = [
  { to: "/transaksi-dtf", Icon: Plus, judul: "Order DTF", sub: "Transaksi baru", cls: "bg-soq-oranye text-soq-biru-tua", testid: "do-cepat-dtf" },
  { to: "/jersey/baru", Icon: Shirt, judul: "Order Jersey", sub: "Transaksi baru", cls: "bg-soq-biru text-white", testid: "do-cepat-jersey" },
  { to: "/pembayaran", Icon: CreditCard, judul: "Catat pembayaran", sub: "DP / pelunasan", cls: "bg-soq-biru-tua text-white", testid: "do-cepat-bayar" },
  { to: "/laporan", Icon: BarChart3, judul: "Laporan", sub: "Penjualan, kas, piutang", cls: "bg-[#EEF1FF] text-soq-biru border border-[#C9CEFF]", testid: "do-cepat-laporan" },
];

const TINDAKAN_WARNA = {
  oranye: { kotak: "bg-[#FFF4E0] border-[#FCD9A0]", angka: "bg-soq-oranye text-soq-biru-tua", tombol: "bg-soq-oranye text-soq-biru-tua" },
  merah: { kotak: "bg-[#FDECEA] border-[#F5C2BC]", angka: "bg-[#B42318] text-white", tombol: "bg-[#B42318] text-white" },
  biru: { kotak: "bg-[#EEF1FF] border-[#C9CEFF]", angka: "bg-soq-biru text-white", tombol: "bg-soq-biru text-white" },
  aman: { kotak: "bg-[#F1FAF5] border-[#CDEBDB]", angka: "bg-[#E7F6EF] text-[#047857]", tombol: "bg-white text-[#047857] border border-[#CDEBDB]" },
};

const AMAN = {
  MENUNGGU_ACC: "Tidak ada yang menunggu ACC.", SIAP_BELUM_LUNAS: "Semua yang siap diambil sudah lunas.",
  LEWAT_RENCANA: "Semua sesuai rencana.", STOK: "Stok aman.", KEPUTUSAN: "Tidak ada yang menunggu keputusan.",
  TARIF_JAHIT: "Semua jenis jahitan sudah bertarif.", SELISIH_QTY: "Tidak ada selisih.", WA_GAGAL: "Semua pesan terkirim.",
};

function Tindakan({ d }) {
  const keputusan = d.perlu_keputusan.daftar.filter((k) => k.jumlah > 0);
  const total = totalTindakan(d.perlu_tindakan);
  return (
    <section data-testid="do-tindakan" className={KARTU}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-bold text-soq-biru-tua">Perlu tindakan</h2>
          <p className="text-xs text-slate-500">Hal yang menunggu keputusan atau tindak lanjut</p>
        </div>
        <span data-testid="do-tindakan-total" className={`rounded-full px-3 py-1 text-xs font-bold ${total ? "bg-[#FDECEA] text-[#B42318]" : "bg-[#E7F6EF] text-[#047857]"}`}>
          {total ? `${total} perlu tindakan` : "Semua aman"}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {d.perlu_tindakan.map((t) => {
          const w = TINDAKAN_WARNA[t.jumlah ? t.warna : "aman"];
          return (
            <div key={t.kunci} data-testid={`do-tindakan-${t.kunci}`} className={`flex flex-col rounded-xl border p-4 ${w.kotak}`}>
              <div className="flex items-start justify-between gap-2">
                <b className="text-sm text-slate-100">{t.label}</b>
                <span className={`inline-flex h-7 min-w-[28px] items-center justify-center rounded-full px-2 text-sm font-bold ${w.angka}`}>{t.jumlah}</span>
              </div>
              <div className="mt-2 flex-1 text-xs text-slate-400 space-y-1">
                {!t.jumlah && <p>{AMAN[t.kunci]}</p>}
                {t.jumlah > 0 && t.kunci === "KEPUTUSAN" && keputusan.map((k) => (
                  <Link key={k.jenis} to={k.tautan} data-testid={`keputusan-${k.jenis}`} className="flex justify-between gap-2 underline-offset-2 hover:underline">
                    <span>{k.label}{k.lewat_batas > 0 && <span className="text-[#B42318]"> · {k.lewat_batas} lewat 2×24 jam</span>}</span><b>{k.jumlah}</b>
                  </Link>
                ))}
                {t.jumlah > 0 && t.kunci !== "KEPUTUSAN" && (
                  <>
                    {t.keterangan && <p>{t.keterangan}</p>}
                    {t.contoh.length > 0 && <p className="font-mono text-slate-500 break-words">{t.contoh.join(" · ")}{t.jumlah > t.contoh.length ? ` · +${t.jumlah - t.contoh.length} lainnya` : ""}</p>}
                  </>
                )}
              </div>
              {t.tautan && (
                <Link to={t.tautan} data-testid={`do-tindakan-buka-${t.kunci}`}
                  className={`mt-3 inline-flex min-h-[40px] items-center justify-center gap-1 rounded-lg text-sm font-semibold ${w.tombol}`}>
                  {t.jumlah ? "Buka" : "Lihat"} <ChevronRight size={15} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PerPos({ d }) {
  const lama = d.produksi_lama;
  return (
    <section data-testid="do-per-pos" className={KARTU}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-bold text-soq-biru-tua">Status produksi per pos</h2>
          <p className="text-xs text-slate-500">Pekerjaan berikutnya per pos dari SPK yang sudah lepas ke produksi</p>
        </div>
        <Link to="/production-monitoring" className="inline-flex items-center gap-1 text-sm font-semibold text-soq-biru">Monitoring <ChevronRight size={15} /></Link>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs text-slate-500">
              <th className="px-2 sm:px-3 py-2 font-semibold">Pos</th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-right">Menunggu</th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-right"><span className="sm:hidden">Proses</span><span className="hidden sm:inline">Sedang diproses</span></th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-right"><span className="sm:hidden">Selesai*</span><span className="hidden sm:inline">Selesai bulan ini</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {d.per_pos.map((p) => (
              <tr key={p.pos_code} data-testid={`do-pos-${p.pos_code}`}>
                <td className="px-2 sm:px-3 py-2.5 text-slate-200">{p.nama}</td>
                <td className="px-2 sm:px-3 py-2.5 text-right tabular-nums">{p.menunggu}</td>
                <td className="px-2 sm:px-3 py-2.5 text-right tabular-nums">{p.sedang}</td>
                <td className="px-2 sm:px-3 py-2.5 text-right tabular-nums">{p.selesai_bulan_ini}</td>
              </tr>
            ))}
            {d.per_pos.length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-slate-500">Belum ada pekerjaan di pos mana pun bulan ini.</td></tr>}
          </tbody>
        </table>
        <p className="mt-1 text-[11px] text-slate-500 sm:hidden">* selesai bulan ini</p>
      </div>
      {/* figures of the old dashboard (kept, owner 2026-10-11) */}
      <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="dashboard-spk">
        {[
          ["stat-spk-total", "SPK aktif", lama.spk.total], ["stat-spk-processing", "Sedang diproses", lama.spk.processing],
          ["stat-spk-done", "SPK selesai (semua)", lama.spk.done], ["stat-spk-cancelled", "SPK dibatalkan", lama.spk.cancelled ?? 0],
          ["stat-prod-rework", "Perlu rework QC", lama.produksi.qc_rework], ["stat-siap-diambil", "Siap diambil", d.siap_diambil],
          ["stat-diambil-hari-ini", "Diambil hari ini", d.diambil_hari_ini],
        ].map(([id, t, v]) => (
          <span key={id} data-testid={id} className="rounded-full border border-slate-800 bg-slate-50 px-3 py-1 text-slate-400">
            {t} <b className="tabular-nums text-slate-200">{v}</b>
          </span>
        ))}
      </div>
    </section>
  );
}

export default function DashboardOwner() {
  const { user } = useAuth();
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      setD(await apiDashboardOwner());
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, []);
  React.useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (!d) {
    return (
      <div data-testid="dashboard-owner" className="py-10 text-center text-sm text-slate-500">
        {error ? <span className="text-red-300">{error}</span> : "Memuat dashboard…"}
      </div>
    );
  }

  const h = d.hari_ini;
  const k = d.kemarin;
  const b = d.bulan_ini;
  const l = d.bulan_lalu;
  const vs = l ? `vs ${labelPeriode(l)}` : "vs bulan lalu";
  const totalSpk = b.spk_selesai + b.spk_diproduksi;

  return (
    <div data-testid="dashboard-owner" className="space-y-5">
      <section data-testid="do-banner" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-soq-biru to-soq-biru-tua p-5 sm:p-7 text-white">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-judul text-[30px] sm:text-[36px] font-extrabold italic uppercase leading-none text-white">
                {salam(jamToko(d.waktu))}, {user?.nama || user?.username || "Owner"}
              </h1>
              <span data-testid="do-waktu" className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs">
                {judulWaktu(d.waktu)} {zonaSingkat()}
              </span>
            </div>
            <p className="mt-2 text-sm text-[#DDE2FF]">Ringkasan usaha hari ini — angka dihitung server dari data asli.</p>
          </div>
          <button type="button" onClick={load} disabled={busy} data-testid="dashboard-refresh"
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-3 text-sm font-semibold text-white disabled:opacity-60">
            <RefreshCw size={15} /> {busy ? "Memuat…" : "Muat ulang"}
          </button>
        </div>
        {error && <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm text-red-300">{error} — angka di bawah dari pemuatan terakhir.</p>}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          <KotakBanner testid="do-order-hari-ini" Icon={ShoppingCart} label="Order hari ini" nilai={h.order}
            catatan={k ? `kemarin ${k.order}` : "kemarin: belum ada data"} />
          <KotakBanner testid="do-omzet-hari-ini" Icon={TrendingUp} label="Omzet hari ini" nilai={rupiah(h.omzet)}
            catatan={k ? `kemarin ${rupiah(k.omzet)}` : "kemarin: belum ada data"} />
          <KotakBanner testid="do-uang-hari-ini" Icon={Wallet} label="Uang masuk hari ini" nilai={rupiah(h.uang_masuk)}
            catatan={k ? `semua akun kas, termasuk kas manual · kemarin ${rupiah(k.uang_masuk)}` : "semua akun kas, termasuk kas manual"} />
          <KotakBanner testid="do-spk-diproduksi" Icon={Factory} label="SPK sedang diproduksi" nilai={h.spk_diproduksi}
            catatan="sudah lepas, belum selesai" />
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KartuAngka testid="do-order-bulan" Icon={ShoppingCart} ikonWarna="bg-[#EEF1FF] text-soq-biru"
          chip={<Chip b={banding(b.order, l?.order ?? null)} label={vs} />} nilai={b.order} judul="Order bulan ini"
          sub={l ? `${labelPeriode(l)}: ${l.order} order` : "nota terbit, tanpa yang batal"} />
        <KartuAngka testid="do-omzet-bulan" Icon={TrendingUp} ikonWarna="bg-[#FFF4E0] text-[#7A4500]"
          chip={<Chip b={banding(b.omzet, l?.omzet ?? null)} label={vs} />} nilai={rupiah(b.omzet)} judul="Omzet bulan ini"
          sub={l ? `${labelPeriode(l)}: ${rupiah(l.omzet)}` : "total nota, tanpa yang batal"} />
        <KartuAngka testid="do-spk-bulan" Icon={CheckCircle2} ikonWarna="bg-[#E7F6EF] text-[#047857]"
          chip={<Chip b={banding(b.spk_selesai, l?.spk_selesai ?? null)} label={vs} />} nilai={b.spk_selesai}
          judul="SPK selesai bulan ini" sub={`${b.spk_diproduksi} SPK masih diproduksi`}>
          <div className="mt-3 flex justify-between text-xs text-slate-500"><span>Selesai</span><b className="text-slate-200">{b.spk_selesai} dari {totalSpk}</b></div>
          <Bilah nilai={persen(b.spk_selesai, totalSpk)} warna="bg-[#047857]" />
        </KartuAngka>
        <KartuAngka testid="do-piutang" Icon={Receipt} ikonWarna="bg-[#FDECEA] text-[#B42318]"
          chip={<span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] font-semibold text-slate-500">saat ini</span>}
          nilai={rupiah(d.piutang.sisa)} judul="Piutang saat ini" sub={`${d.piutang.faktur} faktur belum lunas`}>
          <div className="mt-3 flex justify-between gap-2 text-xs text-slate-500">
            <span>Sudah diambil, belum lunas</span>
            <b className="text-slate-200 whitespace-nowrap">{d.piutang.faktur_sudah_diambil} faktur</b>
          </div>
          <Bilah nilai={persen(d.piutang.sisa_sudah_diambil, d.piutang.sisa)} warna="bg-[#B42318]" />
          {d.piutang.sisa_sudah_diambil > 0 && <div className="mt-1 text-xs text-[#B42318]">{rupiah(d.piutang.sisa_sudah_diambil)}</div>}
        </KartuAngka>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2"><Omzet d={d} /></div>
        <Aktivitas d={d} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section data-testid="do-cepat" className={KARTU}>
          <h2 className="text-[17px] font-bold text-soq-biru-tua">Tombol cepat</h2>
          <p className="text-xs text-slate-500">Pekerjaan yang sering dipakai</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {CEPAT.map((c) => (
              <Link key={c.to} to={c.to} data-testid={c.testid} className={`flex min-h-[96px] flex-col items-center justify-center rounded-xl p-3 text-center ${c.cls}`}>
                <c.Icon size={20} />
                <b className="mt-1 text-sm">{c.judul}</b>
                <span className="text-[11.5px] opacity-80">{c.sub}</span>
              </Link>
            ))}
          </div>
        </section>

        <section data-testid="do-customer" className={KARTU}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-[17px] font-bold text-soq-biru-tua">Customer terbesar</h2>
              <p className="text-xs text-slate-500">Bulan ini, menurut omzet nota</p>
            </div>
            <Link to="/laporan" className="inline-flex items-center gap-1 text-sm font-semibold text-soq-biru">Laporan <ChevronRight size={15} /></Link>
          </div>
          {d.customer_terbesar.length === 0 && <p className="mt-4 text-sm text-slate-500">Belum ada nota bulan ini.</p>}
          <ol className="mt-3 space-y-2">
            {d.customer_terbesar.map((c, i) => (
              <li key={`${c.nama}-${i}`} data-testid="do-customer-baris" className="flex items-center gap-3 rounded-xl bg-slate-50 p-2.5">
                <span className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-soq-biru text-sm font-bold text-white">
                  {inisial(c.nama)}
                  <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-soq-oranye text-[11px] text-soq-biru-tua">{i + 1}</span>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-200">{c.nama}</div>
                  <div className="text-xs text-slate-500">{c.instansi ? "instansi · " : ""}{c.nota} nota</div>
                </div>
                <b className="text-sm tabular-nums text-soq-biru-tua whitespace-nowrap">{rupiah(c.omzet)}</b>
              </li>
            ))}
          </ol>
        </section>

        <section data-testid="kartu-saldo" className={KARTU}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-[17px] font-bold text-soq-biru-tua">Saldo kas</h2>
              <p className="text-xs text-slate-500">Per akun, dihitung dari Buku Kas</p>
            </div>
            <Link to="/kas" className="inline-flex items-center gap-1 text-sm font-semibold text-soq-biru">Buku Kas <ChevronRight size={15} /></Link>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-[#E7F6EF] p-3"><div className="text-[11.5px] text-slate-500">Masuk hari ini</div><b className="tabular-nums text-[#047857]">{rupiah(d.kas.masuk)}</b></div>
            <div className="rounded-xl bg-[#FDECEA] p-3"><div className="text-[11.5px] text-slate-500">Keluar hari ini</div><b className="tabular-nums text-[#B42318]">{rupiah(d.kas.keluar)}</b></div>
          </div>
          <ul className="mt-3 divide-y divide-slate-800">
            {d.kas.akun.map((a) => (
              <li key={a.kode} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-slate-200">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFF4E0] text-[#7A4500]"><Landmark size={15} /></span>
                  <span className="truncate">{a.nama}</span>
                </span>
                <b className="tabular-nums text-soq-biru-tua whitespace-nowrap">{rupiah(a.saldo)}</b>
              </li>
            ))}
          </ul>
          <div className="mt-1 flex justify-between border-t border-slate-800 pt-3 text-sm"><b>Total</b><b className="tabular-nums text-soq-biru-tua">{rupiah(d.kas.saldo_total)}</b></div>
        </section>
      </div>

      <Tindakan d={d} />
      <PerPos d={d} />

      {/* G1-D: phone notifications (was on the Ringkasan Owner page) */}
      <PushPanel testid="ringkasan-push" />
    </div>
  );
}
